import { NotificationService } from './notification.service';
import { effect, Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';

import { LocalTaxonomy, Taxonomy } from '../pages/taxonomy-dashboard-v2/taxonomy.model';
import { EmptySelectionTaxonomy } from '../pages/taxonomy-dashboard-v2/taxonomy.constants';
import { TaxonomyApiService } from './api-services/taxonomy-api.service';

@Injectable({
  providedIn: 'root',
})
export class TaxonomyService {
  private updateTaxonomySource = new BehaviorSubject<void>(undefined);
  private _selectedTaxonomyItem = signal<LocalTaxonomy>({ ...EmptySelectionTaxonomy });
  private _editIndex: number = -1;
  updateTaxonomy$ = this.updateTaxonomySource.asObservable();

  entitityFormFlag: string = '';
  selectedTaxonomy: any;
  selectedEnitity: any = { LinkedEntities: [] };
  enitity: any;

  taxoCheckFlag = signal<string>('default');

  selectedTaxonomyEntitiesList = [];

  constructor(private router: Router, private taxonomyApiService: TaxonomyApiService, public notificationService: NotificationService) {
    effect(
      () => {
        const selected = this._selectedValue();
        if (selected) {
          // Only navigate to taxonomy page if user is already on a taxonomy-related route
          // This prevents unwanted redirects when user is on insights dashboard
          const currentRoute = this.router.url;
          if (currentRoute.includes('/taxonomy-dashboard')) {
            this.navigateToTaxonomyPage(selected.taxonomy_id);
          }
        }
      },
      { allowSignalWrites: true }
    );

    // Check lock status on initialization
    this.checkLockStatus();

    // Add storage event listener to handle changes from other components/tabs
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === this.SELECTED_TAXONOMY_KEY) {
        this.checkLockStatus();
      }
    });
  }

  // === PERSISTENCE ===
  private readonly SELECTED_TAXONOMY_KEY = 'selectedTaxonomy';

  private getSavedSelectedTaxonomy(): {
    taxonomy_id: string;
    taxonomy_name: string;
    version_history_details: any[];
    selected_taxonomy_version: any;
  } | null {
    const stored = sessionStorage.getItem(this.SELECTED_TAXONOMY_KEY);
    try {
      const parsed = stored ? JSON.parse(stored) : null;
      if (parsed && !parsed.version_history_details) {
        // If old data format exists, return null to force fresh data load
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private _selectedValue = signal<{
    taxonomy_id: string;
    taxonomy_name: string;
    version_history_details: any[];
    selected_taxonomy_version: any;
  } | null>(this.getSavedSelectedTaxonomy());

  setSelectedValue(value: {
    taxonomy_id: string;
    taxonomy_name: string;
    version_history_details: any[];
    selected_taxonomy_version: any;
  } | null) {
    this.selectedTaxonomy = value || {};
    this._selectedValue.set(value);

    if (value) {
      // Ensure immediate sessionStorage update
      sessionStorage.setItem(this.SELECTED_TAXONOMY_KEY, JSON.stringify(value));
      // Trigger immediate update for other components
      this.triggerTaxonomyUpdate();
    } else {
      sessionStorage.removeItem(this.SELECTED_TAXONOMY_KEY);
    }

    // Call checkLockStatus after updating the selected taxonomy
    this.checkLockStatus();
  }

  getSelectedValue() {
    return this._selectedValue;
  }

  public get taxonomyItem(): WritableSignal<LocalTaxonomy> {
    return this._selectedTaxonomyItem;
  }

  setTaxonomyItem(data: LocalTaxonomy): void {
    this._selectedTaxonomyItem.set(data);
    this._editIndex = this._taxonomiesData().findIndex((obj) => obj.taxonomy_id == data.taxonomy_id);
  }

  public get taxonomyData(): WritableSignal<LocalTaxonomy[]> {
    return this._taxonomiesData;
  }

  setTaxonomyData(data: Taxonomy[]): void {
    const dataWIthIndex = data.map((item, index) => ({ id: index + 1, ...item }));
    this._taxonomiesData.set(dataWIthIndex);
  }

  saveTaxonomyData(data: LocalTaxonomy) {
    const newData = [...this._taxonomiesData(), data];
    this.setTaxonomyData(newData);
  }

  _list_of_taxonomies = signal<{ taxonomy_id: string; taxonomy_name: string }[]>([]);
  _taxonomiesData = signal<any[]>([]);

  getListOfTaxonomies(res: any) {
    this._taxonomiesData.set(res.data);

    this._list_of_taxonomies.set(
      res.data.map((item: any) => ({
        taxonomy_id: item.taxonomy_id,
        taxonomy_name: item.taxonomy_name,
      }))
    );
  }

  removeTaxonomyFromStorage(taxonomyId: string) {
    const localData = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}');
    if (localData.taxonomy_id === taxonomyId) {
      sessionStorage.removeItem('selectedTaxonomy');
      this.setSelectedValue(null);
    }
  }

  getTaxonomiesData() {
    return this._taxonomiesData;
  }

  setTaxonomiesData(res: any) {
    this._taxonomiesData.set(res.data);
  }

  triggerTaxonomyUpdate() {
    this.updateTaxonomySource.next();
  }

  navigateToTaxonomyPage(id: string): void {
    this.router.navigate(['/taxonomy-dashboard', id]);
  }

  loadTaxonomy() {
    this.taxonomyApiService.getTaxonomyV2({}).subscribe((res) => {
      this.getListOfTaxonomies(res);
      this.setTaxonomiesData(res);
    });
  }

  configureValue: any;

  versionHistoryById() {
    const localDataRaw = sessionStorage.getItem('selectedTaxonomy') || '{}';
    const localData = JSON.parse(localDataRaw);
    const local_taxonomy_id = String(localData.taxonomy_id || '');
    const currentRecordId = localData.selected_taxonomy_version?.record_id;

    const body = { taxonomy_id: local_taxonomy_id };

    this.taxonomyApiService.getVersionHistoryById(body).subscribe({
      next: (res) => {
        const responseData = res.data;
        const responseTaxonomyId = responseData?.taxonomy_id;

        if (responseTaxonomyId && responseTaxonomyId === local_taxonomy_id) {
          const updatedVersionHistory = responseData.version_history_details || [];

          // Find the version with the same record_id if it exists
          const matchingVersion = currentRecordId ?
            updatedVersionHistory.find((v: any) => v.record_id === currentRecordId) : null;

          // Update the selectedTaxonomy in sessionStorage with new version history
          const updatedLocalData = {
            ...localData,
            version_history_details: updatedVersionHistory,
            selected_taxonomy_version: matchingVersion || updatedVersionHistory[0] || null
          };

          // Update sessionStorage
          sessionStorage.setItem('selectedTaxonomy', JSON.stringify(updatedLocalData));

          // Update the service's selected value
          this.setSelectedValue(updatedLocalData);

          // Trigger the updateTaxonomy$ observable to refresh the entity hub component
          this.triggerTaxonomyUpdate();
        } else {
          // console.warn("Taxonomy ID mismatch. No update performed.");
        }
      },
      error: (err) => {
        this.notificationService.error('Version', 'Please reload the page.');
      },
    });
  }

  lockStatus: boolean = false;

  checkLockStatus(){
    const localData = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}');
    const local_taxonomy_version_status = String(localData.selected_taxonomy_version?.status || '');

    // Lock if status is not draft
    if(local_taxonomy_version_status == "published" || local_taxonomy_version_status == ""){
      this.lockStatus = true;
    } else {
      this.lockStatus = false;
    }
  }

  updateTaxoCheckFlag(value: string) {
    this.taxoCheckFlag.set(value);
  }

  /**
   * Clear selected taxonomy when user navigates away from taxonomy pages
   * This prevents unwanted navigation effects when on other routes
   */
  clearSelectedTaxonomyIfNotOnTaxonomyRoute(): void {
    const currentRoute = this.router.url;
    if (!currentRoute.includes('/taxonomy-dashboard')) {
      // Clear selected taxonomy when not on taxonomy routes
      this.setSelectedValue(null);
    }
  }
}
